export const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
];

export const VISIT_UTMS_STORAGE_KEY = 'taptech.visitUtms';

export function normalizeVisitUtmValues(raw) {
  const list = Array.isArray(raw) ? raw : raw == null ? [] : [raw];
  const values = [];

  list.forEach((value) => {
    if (typeof value === 'string' && value && !values.includes(value)) {
      values.push(value);
    }
  });

  return values;
}

function compactVisitUtms(utms) {
  const compacted = {};

  UTM_PARAM_KEYS.forEach((key) => {
    const values = normalizeVisitUtmValues(utms[key]);
    if (values.length === 1) {
      compacted[key] = values[0];
    } else if (values.length > 1) {
      compacted[key] = values;
    }
  });

  return compacted;
}

/**
 * Union-merge one utm_* key: never replace in either direction.
 * Destination values stay first; distinct visit values are appended;
 * identical visit values are omitted so they are emitted once.
 * Empty destination values are not treated as real UTMs, so they do not
 * block a visitor value; mergeVisitUtmsOntoUrl still keeps the empty param.
 */
export function unionUtmValues(destinationValues, visitValues) {
  const existing = Array.isArray(destinationValues)
    ? destinationValues.filter((value) => typeof value === 'string' && value)
    : [];
  const result = existing.slice();

  normalizeVisitUtmValues(visitValues).forEach((value) => {
    if (!result.includes(value)) {
      result.push(value);
    }
  });

  return result;
}

export function extractVisitUtmsFromSearch(search) {
  const query = search == null ? '' : String(search);
  const params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
  const utms = {};

  UTM_PARAM_KEYS.forEach((key) => {
    const values = normalizeVisitUtmValues(params.getAll(key));
    if (values.length === 1) {
      utms[key] = values[0];
    } else if (values.length > 1) {
      utms[key] = values;
    }
  });

  return utms;
}

function sessionStorageOrNull() {
  try {
    if (typeof sessionStorage === 'undefined' || sessionStorage == null) {
      return null;
    }

    return sessionStorage;
  } catch {
    return null;
  }
}

function readStoredVisitUtms() {
  try {
    const storage = sessionStorageOrNull();
    if (!storage) {
      return {};
    }

    const raw = storage.getItem(VISIT_UTMS_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    return compactVisitUtms(parsed);
  } catch {
    return {};
  }
}

function defaultSearch() {
  return typeof window !== 'undefined' && window.location ? window.location.search : '';
}

/**
 * Pure visit-UTM snapshot: landing-URL values win when present, otherwise
 * stored values. Does not write sessionStorage.
 */
export function readVisitUtms(search = defaultSearch()) {
  const fromUrl = extractVisitUtmsFromSearch(search);
  if (Object.keys(fromUrl).length > 0) {
    return fromUrl;
  }

  return readStoredVisitUtms();
}

export function persistVisitUtms(utms) {
  const next = compactVisitUtms(utms || {});
  if (Object.keys(next).length === 0) {
    return;
  }

  try {
    const storage = sessionStorageOrNull();
    if (!storage) {
      return;
    }

    const nextRaw = JSON.stringify(next);
    if (storage.getItem(VISIT_UTMS_STORAGE_KEY) === nextRaw) {
      return;
    }

    storage.setItem(VISIT_UTMS_STORAGE_KEY, nextRaw);
  } catch {
    // Ignore quota / private-mode failures; in-memory values still apply this visit.
  }
}

export function captureVisitUtms(search = defaultSearch()) {
  const utms = readVisitUtms(search);
  persistVisitUtms(utms);
  return utms;
}

function schemeOf(url) {
  const match = String(url).trim().match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
  return match ? match[1].toLowerCase() : '';
}

function isOutboundHttpUrl(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }

  const scheme = schemeOf(trimmed);
  if (scheme && scheme !== 'http' && scheme !== 'https') {
    return false;
  }

  // Path-relative, root-relative, and bare paths are same-site even with no window.
  if (!scheme && !trimmed.startsWith('//')) {
    return false;
  }

  try {
    const hasWindow = typeof window !== 'undefined' && window.location;
    const base = hasWindow && window.location.href ? window.location.href : 'http://localhost/';
    const parsed = new URL(trimmed, base);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false;
    }

    if (hasWindow && window.location.hostname) {
      return parsed.hostname !== window.location.hostname;
    }

    return true;
  } catch {
    return false;
  }
}

export function mergeVisitUtmsOntoUrl(url, visitUtms) {
  if (!url || typeof url !== 'string' || !isOutboundHttpUrl(url)) {
    return url;
  }

  const utms = visitUtms && typeof visitUtms === 'object' ? visitUtms : {};
  const keysToApply = UTM_PARAM_KEYS.filter((key) => normalizeVisitUtmValues(utms[key]).length > 0);
  if (keysToApply.length === 0) {
    return url;
  }

  const hashIndex = url.indexOf('#');
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const queryIndex = withoutHash.indexOf('?');
  const base = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const searchParams = new URLSearchParams(queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : '');

  keysToApply.forEach((key) => {
    const existing = searchParams.getAll(key);
    normalizeVisitUtmValues(utms[key]).forEach((value) => {
      // Keep empty destination params as-is; only skip append when the exact
      // visit value is already present (empty '' does not block a real value).
      if (!existing.includes(value)) {
        searchParams.append(key, value);
        existing.push(value);
      }
    });
  });

  const query = searchParams.toString();
  return query ? `${base}?${query}${hash}` : `${base}${hash}`;
}

export function withVisitUtms(url, visitUtms) {
  return mergeVisitUtmsOntoUrl(url, visitUtms);
}
